import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import { crmPostventaRolloApi } from "../crmPostventaRolloApi";
import Alert from "../../crmpostventa-shared/components/Alert";
import Modal from "../../crmpostventa-shared/components/Modal";
import { NO_MANAGE_TITLE } from "../components/RowActions";
import { formatRollos, todayIso } from "../rolloFormat";
import { ActionPanel, CancelForm, ClosedSummary, GestionHeader, Stepper, Timeline } from "./GestionarParts";
import {
  ACCIONES,
  CANCEL_FIELD_IDS,
  CONFLICT_TITLE,
  EMPTY_CANCEL_FORM,
  PRIMARY_LABELS,
  accionFor,
  buildAccionPayload,
  buildInitialForm,
  cancelFieldId,
  createCorrelationId,
  describeAccionError,
  fieldId,
  firstErrorKey,
  freshEntryIds,
  isClosed,
  isConflict,
  toastTitleFor,
  validateAccion,
  validateCancelacion,
} from "./gestionarModel";

const TITLE_ID = "crm-rollo-gestionar-title";
const CANCEL_TITLE_ID = "crm-rollo-cancelar-title";

const withoutKey = (errors, key) => {
  if (!errors[key]) return errors;
  const next = { ...errors };
  delete next[key];
  return next;
};

/**
 * "Gestionar" of a Rollo gestión (design RolloManagePrep, S5/S6/S9). Loads GET gestiones/{id}, shows the header,
 * the stepper, the action of the current estado and the timeline, and posts the action with the current
 * versionToken and one correlationId per submission (a retry of the same data reuses it). "Cancelar gestión"
 * opens a confirmation on top. A 409 shows the conflict alert: "Recargar gestión" reloads the detail, keeps what
 * the asesor typed and highlights the new timeline entries. Without manage rights, or once closed, it is read-only.
 *
 * onDone({ gestionId, title, text }) runs after a successful action and is expected to close the modal.
 */
export default function GestionarModal({ gestionId, row, darkMode = false, diasParaDemora = null, onClose, onDone }) {
  const [detail, setDetail] = useState({ status: "loading", data: null, error: null });
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [outcome, setOutcome] = useState(ACCIONES.ENTREGAR);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [freshIds, setFreshIds] = useState([]);
  const [focusRequest, setFocusRequest] = useState(0);
  const [cancel, setCancel] = useState({ open: false, form: EMPTY_CANCEL_FORM, errors: {}, error: null, focus: 0 });
  const attemptRef = useRef(null);
  const mountedRef = useRef(true);
  const firstFocusRef = useRef(false);
  // Timeline of the last load, to tell which entries a reload adds.
  const timelineRef = useRef([]);
  const today = todayIso();

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const data = detail.data;
  const gestion = data?.gestion || row || null;
  const versionToken = data?.versionToken || data?.gestion?.versionToken || null;
  const closed = isClosed(gestion?.estado) || gestion?.activo === false;
  const canManage = detail.status === "done" && data?.puedeGestionar !== false && !closed;
  const accion = canManage ? accionFor(gestion?.estado, outcome) : null;

  // First load builds the form from the gestión; a reload (conflict) keeps it and marks the new timeline entries.
  const load = useCallback(
    async (reload = false) => {
      if (!reload) setDetail({ status: "loading", data: null, error: null });
      try {
        const response = await crmPostventaRolloApi.obtenerGestion(gestionId);
        if (!mountedRef.current) return false;
        setFreshIds(reload ? freshEntryIds(timelineRef.current, response?.timeline) : []);
        timelineRef.current = response?.timeline || [];
        setDetail({ status: "done", data: response || {}, error: null });
        setForm((current) => (reload && current ? current : buildInitialForm(response?.gestion, todayIso())));
        return true;
      } catch (loadError) {
        if (!mountedRef.current) return false;
        if (!reload) setDetail({ status: "error", data: null, error: loadError });
        return false;
      }
    },
    [gestionId]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Focus the first control of the action once the detail is shown (design), and the first invalid field after
  // a failed submit.
  useEffect(() => {
    if (firstFocusRef.current || detail.status !== "done") return;
    firstFocusRef.current = true;
    document.querySelector(".crm-rollo__action input, .crm-rollo__action textarea, .crm-rollo__action select")?.focus();
  }, [detail.status]);

  useEffect(() => {
    if (!focusRequest) return;
    const key = firstErrorKey(errors);
    if (key) document.getElementById(fieldId(key))?.focus();
    // Only a new submit moves the focus, not every error change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  useEffect(() => {
    if (!cancel.open) return;
    const key = firstErrorKey(cancel.errors, CANCEL_FIELD_IDS) || "motivo";
    document.getElementById(cancelFieldId(key))?.focus();
    // Opening the confirmation and each failed confirm move the focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancel.open, cancel.focus]);

  // "Otro" stops being the motivo: an observación that was only missing is fine again.
  const changeField = (key, value) => {
    const nextForm = { ...form, [key]: value };
    setForm(nextForm);
    setErrors((current) => {
      const next = withoutKey(current, key);
      return key === "motivo" && next.observacion && !validateAccion(accion, nextForm, today).observacion
        ? withoutKey(next, "observacion")
        : next;
    });
  };

  const changeOutcome = (next) => {
    if (next === outcome) return;
    setOutcome(next);
    setErrors({});
    setForm((current) => ({ ...current, observacion: "" }));
  };

  // One correlationId per submission: the same body (same data and versionToken) reuses it, so a retry after a
  // network error gets the server's idempotent answer instead of a second action.
  const send = async (payload, fromCancel = false) => {
    const body = { ...payload, versionToken };
    const key = JSON.stringify(body);
    if (attemptRef.current?.key !== key) {
      attemptRef.current = { key, correlationId: createCorrelationId() };
    }

    setSaving(true);
    setSubmitError(null);
    setCancel((current) => ({ ...current, error: null }));
    try {
      await crmPostventaRolloApi.ejecutarAccion(gestionId, { ...body, correlationId: attemptRef.current.correlationId });
      if (!mountedRef.current) return;
      onDone?.({ gestionId, title: toastTitleFor(payload), text: gestion?.nombreComercio || "" });
    } catch (actionError) {
      if (!mountedRef.current) return;
      setSaving(false);
      if (isConflict(actionError)) {
        setConflict({ message: actionError?.message || "" });
        setCancel((current) => ({ ...current, open: false }));
        return;
      }
      const described = describeAccionError(actionError);
      if (fromCancel) setCancel((current) => ({ ...current, error: described }));
      else setSubmitError(described);
    }
  };

  const submit = () => {
    if (saving || !accion || !form) return;
    const nextErrors = validateAccion(accion, form, today);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setFocusRequest((count) => count + 1);
      return;
    }
    send(buildAccionPayload(accion, form));
  };

  const reload = async () => {
    setReloading(true);
    setSubmitError(null);
    const ok = await load(true);
    if (!mountedRef.current) return;
    setReloading(false);
    if (ok) {
      setConflict(null);
    } else {
      setSubmitError({ title: "No pudimos recargar la gestión", text: "Probá de nuevo en unos segundos." });
    }
  };

  const openCancel = () => setCancel((current) => ({ ...current, open: true, errors: {}, error: null }));
  const closeCancel = () => setCancel((current) => ({ ...current, open: false }));

  const changeCancelField = (key, value) =>
    setCancel((current) => {
      const nextForm = { ...current.form, [key]: value };
      const nextErrors = withoutKey(current.errors, key);
      const clearObservacion = key === "motivo" && nextErrors.observacion && !validateCancelacion(nextForm).observacion;
      return { ...current, form: nextForm, errors: clearObservacion ? withoutKey(nextErrors, "observacion") : nextErrors };
    });

  const confirmCancel = () => {
    if (saving) return;
    const nextErrors = validateCancelacion(cancel.form);
    if (Object.keys(nextErrors).length) {
      setCancel((current) => ({ ...current, errors: nextErrors, focus: current.focus + 1 }));
      return;
    }
    send(buildAccionPayload(ACCIONES.CANCELAR, cancel.form), true);
  };

  const renderMain = () => {
    if (detail.status === "loading") {
      return (
        <div className="crm-rollo__context-skel" aria-busy="true" aria-label="Cargando la gestión">
          {["60%", "85%", "45%", "70%"].map((width) => (
            <span key={width} className="crm-rollo__skel crm-rollo__skel--tall" style={{ width }} aria-hidden="true" />
          ))}
        </div>
      );
    }

    if (detail.status === "error") {
      return (
        <div className="crm-rollo__note crm-rollo__note--error" role="alert">
          <span className="crm-rollo__note-ico" aria-hidden="true">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </span>
          <b>No pudimos cargar la gestión</b>
          <p>{detail.error?.message || "Probá de nuevo en unos segundos."}</p>
          <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" onClick={() => load()}>
            <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
            Reintentar
          </button>
        </div>
      );
    }

    return (
      <>
        <Stepper estado={gestion?.estado} />
        {conflict ? (
          <div className="crm-rollo__conflict">
            <Alert tone="error" role="alert" title={CONFLICT_TITLE}>
              {conflict.message && conflict.message !== CONFLICT_TITLE ? `${conflict.message} ` : ""}
              Recargá la gestión para ver los cambios. Lo que cargaste en el formulario se mantiene.
            </Alert>
            <div>
              <button
                type="button"
                className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm"
                disabled={reloading}
                onClick={reload}
              >
                <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
                Recargar gestión
              </button>
            </div>
          </div>
        ) : null}
        {submitError ? (
          <Alert tone="error" role="alert" title={submitError.title}>
            {submitError.text}
          </Alert>
        ) : null}
        {closed ? <ClosedSummary gestion={gestion} /> : null}
        {!closed && !canManage ? <Alert tone="info" title="Solo lectura">{NO_MANAGE_TITLE}</Alert> : null}
        {accion && form ? (
          <ActionPanel
            accion={accion}
            gestion={gestion}
            form={form}
            errors={errors}
            saving={saving}
            today={today}
            onOutcomeChange={changeOutcome}
            onChange={changeField}
          />
        ) : null}
      </>
    );
  };

  const closeButton = (
    <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" disabled={saving} onClick={onClose}>
      Cerrar
    </button>
  );

  const footer = accion ? (
    <>
      <button type="button" className="crm-rollo__link crm-rollo__link--danger" disabled={saving} onClick={openCancel}>
        Cancelar gestión
      </button>
      <span className="crm-rollo__grow" />
      {closeButton}
      <button
        type="button"
        className="crm-rollo__btn crm-rollo__btn--primary"
        disabled={saving}
        aria-busy={saving || undefined}
        onClick={submit}
      >
        {saving ? (
          <>
            <span className="crm-rollo__spinner" aria-hidden="true" />
            Guardando…
          </>
        ) : (
          PRIMARY_LABELS[accion]
        )}
      </button>
    </>
  ) : (
    <>
      <span className="crm-rollo__grow" />
      {closeButton}
    </>
  );

  return (
    <>
      <Modal
        titleId={TITLE_ID}
        title={gestion?.nombreComercio || "Gestión de rollo"}
        stepLabel={`Gestión de rollo · ${closed ? "Finalizada" : "Activa"}`}
        size="lg"
        darkMode={darkMode}
        closeDisabled={saving || cancel.open}
        onClose={onClose}
        headExtra={gestion ? <GestionHeader gestion={gestion} diasParaDemora={diasParaDemora} /> : null}
        bodyClassName="crm-rollo__manage"
        footer={footer}
      >
        <div className="crm-rollo__manage-main">{renderMain()}</div>
        <aside className="crm-rollo__manage-side" aria-label="Línea de tiempo">
          {detail.status === "done" ? <Timeline entries={data?.timeline} gestion={gestion} freshIds={freshIds} /> : null}
        </aside>
      </Modal>

      {cancel.open ? (
        <Modal
          titleId={CANCEL_TITLE_ID}
          title="¿Cancelar esta gestión?"
          role="alertdialog"
          size="xs"
          darkMode={darkMode}
          closeDisabled={saving}
          onClose={closeCancel}
          headExtra={
            <span className="crm-rollo__s">{`${gestion?.nombreComercio || ""} · ${formatRollos(gestion?.cantidadSolicitada)}`}</span>
          }
          footer={
            <>
              <span className="crm-rollo__grow" />
              <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" disabled={saving} onClick={closeCancel}>
                Volver
              </button>
              <button
                type="button"
                className="crm-rollo__btn crm-rollo__btn--danger"
                disabled={saving}
                aria-busy={saving || undefined}
                onClick={confirmCancel}
              >
                {saving ? (
                  <>
                    <span className="crm-rollo__spinner" aria-hidden="true" />
                    Cancelando…
                  </>
                ) : (
                  "Cancelar gestión"
                )}
              </button>
            </>
          }
        >
          <CancelForm
            gestion={gestion}
            form={cancel.form}
            errors={cancel.errors}
            saving={saving}
            submitError={cancel.error}
            onChange={changeCancelField}
          />
        </Modal>
      ) : null}
    </>
  );
}
